import { Address, Country } from 'types/form';
import { apiRequest, apiSecureRequest, investmentEntityRequest, webRequest } from '@utils/request';
import axios, { AxiosResponse } from 'axios';
import {
	EmailSubscriptionDetails,
	PhoneData,
	UpdateEmailGroupSubscription,
	User,
	UserCheckoutDetails,
	UserSignupForm,
	UserTokenResponse
} from 'types/user';
import { CheckoutSelections } from 'types/checkout';
import { UpdateSsnType } from 'types/account';

function setSelectedEntityForUser(userData: User): void {
	if (userData.entityDetails && userData.entityDetails.investmentEntities) {
		userData.entityDetails.investmentEntities = userData.entityDetails.investmentEntities.map(
			(investmentEntity) => {
				investmentEntity.selected =
					investmentEntity.investmentEntityId === window.fundriseApp.investmentEntityId;
				switch (investmentEntity.entityType) {
					case 'INDIVIDUAL':
						investmentEntity.entityTypeLabel = 'Individual';
						break;
					case 'JOINT_ACCOUNT':
						investmentEntity.entityTypeLabel = 'Joint Account';
						break;
					case 'IRA':
						investmentEntity.entityTypeLabel = 'Individual Retirement Account';
						break;
					case 'LLC':
						investmentEntity.entityTypeLabel = 'Investment Company';
						break;
					case 'TRUST':
						investmentEntity.entityTypeLabel = 'Trust';
						break;
					default:
						break;
				}
				return investmentEntity;
			}
		);
		if (userData.entityDetails.investmentEntities.length === 1) {
			userData.entityDetails.investmentEntities[0].selected = true;
		}
	}
}

export const createUser = async (userSignupform: UserSignupForm): Promise<UserTokenResponse> => {
	const response: AxiosResponse = await apiRequest.post(`/user`, userSignupform);
	return {
		accessToken: response.data.accessToken.access_token,
		refreshToken: response.data.accessToken.refresh_token,
		expiresIn: response.data.accessToken.expires_in
	};
};

export const syncNewUserWithSession = async (userTokenResponse: UserTokenResponse): Promise<void> => {
	await webRequest.post(`/sync-user-session`, userTokenResponse);
};

export const updateUserInSession = async (): Promise<void> => {
	await webRequest.post(`/update-user-in-session`);
};

export const isUserVerified = async (): Promise<boolean> => {
	const response: AxiosResponse = await webRequest.get('/user-is-verified');
	return response.data;
};

export const getUser = async (): Promise<User> => {
	const response: AxiosResponse = await apiSecureRequest.get(`/user`);
	const userData: User = response.data;
	setSelectedEntityForUser(userData);
	return userData;
};

export const getEmailVerificationStatus = async (): Promise<boolean> => {
	const response: AxiosResponse = await apiSecureRequest.get(`user/email-verified`);
	return response.data.emailVerified;
};

export const getPhoneData = async (): Promise<PhoneData> => {
	const response: AxiosResponse = await apiSecureRequest.get(`user/phone-numbers`);
	return response.data;
};

export const getAddress = async (): Promise<Address | undefined> => {
	const response: AxiosResponse = await investmentEntityRequest.get(`/current-address`, {
		suppressToast: (response: AxiosResponse) => response.status === 404
	});
	return response.data;
};

export const setUserFirstAndLastNames = async (firstName: string, lastName: string): Promise<void> => {
	await apiSecureRequest.post(`/user/set-name`, {
		firstName: firstName,
		lastName: lastName
	});
};

export const getUserCheckoutDetails = async (): Promise<UserCheckoutDetails> => {
	const response: AxiosResponse = await investmentEntityRequest.get(`/checkout-details`);
	return response.data;
};

export async function getOrderFormDetails(): Promise<{
	details: UserCheckoutDetails;
	countries: Array<Country>;
	selections: CheckoutSelections;
}> {
	const responses = await axios.all([
		investmentEntityRequest.get('/checkout-details'),
		apiRequest.get('/countries'),
		webRequest.get('/reits/checkout/selections')
	]);
	return {
		details: responses[0].data,
		countries: responses[1].data,
		selections: responses[2].data
	};
}

export const setUserCountry = async (selections: {
	citizenshipCountryCode: string;
	residenceCountryCode: string;
}): Promise<void> => {
	await apiSecureRequest.post(`/user/set-country`, selections);
};

export const setUserSsn = async (ssn: string): Promise<UpdateSsnType> => {
	const response = await apiSecureRequest.post('/user/set-ssn', { ssn: ssn });
	const hasSubmittedSameSsn = response.data.hasSubmittedSameSsn;
	return hasSubmittedSameSsn ? 'confirm' : 'update';
};

export const submitMobileVerificationCode = async (
	phoneNumber: string,
	phoneNumberId: string,
	verificationCode: string
): Promise<void> => {
	await apiSecureRequest.post(`/account/security/mobile-number`, {
		phoneNumberId: phoneNumberId,
		newPhoneNumber: phoneNumber,
		authCode: verificationCode
	});
};

export const sendMobileVerificationCode = async (phoneNumber: string): Promise<void> => {
	await apiSecureRequest.post(`/account/security/mobile-number/send-code`, { newPhoneNumber: phoneNumber });
};

export const resendMobileVerificationCode = async (phoneNumber: string): Promise<void> => {
	await apiSecureRequest.post(`/account/security/mobile-number/resend-code`, { newPhoneNumber: phoneNumber });
};

export const getEmailSubscriptions = async (): Promise<EmailSubscriptionDetails> => {
	const response: AxiosResponse = await apiSecureRequest.get(`/notifications/email-subscriptions`);
	return response.data;
};

export const updateEmailSubscriptions = async (
	updateEmailGroupSubscriptions: Array<UpdateEmailGroupSubscription>
): Promise<void> => {
	await apiSecureRequest.post(`/notifications/email-subscriptions`, {
		updateEmailGroupSubscriptions: updateEmailGroupSubscriptions
	});
};

export const updateMobileNumber = async (payload: { phoneNumberId: string }): Promise<void> => {
	await apiSecureRequest.post(`/account/security/mobile-number/`, payload);
};

export const verifyPassword = async (password: string): Promise<string> => {
	const response = await webRequest.post(`/account/security/verify-password`, { password });
	return response.data.accessToken;
};

export const isPasswordVerified = async (): Promise<boolean> => {
	const response: AxiosResponse = await webRequest.get(`/account/security/verify-password/status`);
	return response.data;
};

export const removePasswordVerification = async (): Promise<void> => {
	await webRequest.post(`/account/security/verify-password/remove`);
};
